// src/pages/SettingsPage.jsx
import React, { useState, useContext } from 'react';
import Header from '../components/common/Header';
import Sidebar from '../components/common/Sidebar';
import { ThemeContext } from '../context/ThemeContext';
import { UserContext } from '../context/UserContext';

const SettingsPage = () => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { user, userType, upgradeToPro } = useContext(UserContext);
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      push: true,
      weeklyDigest: true,
      aiLimits: true,
    },
    privacy: {
      publicProfile: false,
      showEmail: false,
      allowTracking: true,
    },
    editor: {
      autoSave: true,
      spellCheck: true,
      markdown: false,
      fontSize: 'medium',
    },
    integrations: {
      zoom: user?.plan === 'pro',
      googleDrive: false,
      dropbox: false,
    },
  });

  const [isExporting, setIsExporting] = useState(false);

  const handleToggle = (category, key) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: !prev[category][key],
      },
    }));
  };

  const handleExportData = async () => {
    setIsExporting(true);
    // Simulate export process
    setTimeout(() => {
      alert('Data export started. You will receive an email with your data shortly.');
      setIsExporting(false);
    }, 1500);
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      alert('Account deletion initiated. Confirmation email sent.');
    }
  };

  return (
    <div className={`settings-page ${theme === 'dark' ? 'dark' : ''}`}>
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 w-full overflow-x-hidden">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
              <p className="text-gray-600 dark:text-gray-300 mt-2">
                Manage your application preferences and account settings
              </p>
            </div>

            <div className="space-y-8">
              {/* Appearance */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Appearance</h2>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">Theme</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Switch between light and dark mode
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {theme === 'light' ? 'Light' : 'Dark'}
                    </span>
                    <button
                      onClick={toggleTheme}
                      className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 dark:bg-gray-700"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          theme === 'dark' ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Notifications */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Notifications</h2>
                <div className="space-y-6">
                  {Object.entries(settings.notifications).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {key === 'email' && 'Receive email notifications'}
                          {key === 'push' && 'Browser push notifications'}
                          {key === 'weeklyDigest' && 'Weekly summary of your activity'}
                          {key === 'aiLimits' && 'Notifications when AI search limits are reached'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleToggle('notifications', key)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                          value ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                            value ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Editor Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Editor Settings</h2>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">Auto-save</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Automatically save changes to notes
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggle('editor', 'autoSave')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                        settings.editor.autoSave ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          settings.editor.autoSave ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div>
                    <label className="block font-medium text-gray-900 dark:text-white mb-2">
                      Editor Font Size
                    </label>
                    <select
                      value={settings.editor.fontSize}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        editor: { ...prev.editor, fontSize: e.target.value }
                      }))}
                      className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Integrations */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Integrations</h2>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 dark:text-white">Zoom Integration</h3>
                        {user?.plan === 'pro' ? (
                          <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded">
                            PRO
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                            UPGRADE
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Automatically import meeting notes from Zoom
                      </p>
                      {user?.integrations?.zoom?.connected && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          ✓ Connected as {user.integrations.zoom.email || 'your Zoom account'}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        if (user?.plan !== 'pro') {
                          alert('Please upgrade to Pro to enable Zoom integration.');
                          return;
                        }
                        
                        if (user?.integrations?.zoom?.connected) {
                          // Disconnect
                          if (window.confirm('Are you sure you want to disconnect Zoom?')) {
                            try {
                              const zoomService = await import('../services/zoomService');
                              await zoomService.default.disconnect();
                              alert('Zoom disconnected successfully.');
                              window.location.reload();
                            } catch (err) {
                              alert('Failed to disconnect Zoom. Please try again.');
                            }
                          }
                        } else {
                          // Connect
                          try {
                            const zoomService = await import('../services/zoomService');
                            const res = await zoomService.default.connect();
                            if (res?.data?.url) {
                              window.location.href = res.data.url;
                            } else {
                              alert('Unable to get Zoom authorization URL. Please try again.');
                            }
                          } catch (err) {
                            alert('Failed to connect to Zoom. Please try again.');
                          }
                        }
                      }}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        user?.plan !== 'pro'
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                          : user?.integrations?.zoom?.connected
                          ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-200'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      disabled={user?.plan !== 'pro'}
                    >
                      {user?.integrations?.zoom?.connected ? 'Disconnect' : 'Connect to Zoom'}
                    </button>
                  </div>

                  {/* Add more integrations here */}
                </div>
              </div>

              {/* Subscription & Billing */}
              {userType === 'free' && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Subscription & Billing</h2>
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 text-white">
                    <h3 className="text-xl font-bold mb-4">Upgrade to Pro</h3>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-center">
                        <span className="mr-2">✓</span> Unlimited AI Searches
                      </li>
                      <li className="flex items-center">
                        <span className="mr-2">✓</span> Zoom Integration
                      </li>
                      <li className="flex items-center">
                        <span className="mr-2">✓</span> No Ads
                      </li>
                      <li className="flex items-center">
                        <span className="mr-2">✓</span> Advanced Analytics
                      </li>
                    </ul>
                    <button
                      onClick={upgradeToPro}
                      className="w-full bg-white text-purple-600 font-bold py-3 rounded-lg hover:bg-gray-100 transition"
                    >
                      Upgrade to Pro
                    </button>
                  </div>
                </div>
              )}

              {/* Data Management */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Data Management</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Export Data</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      Download all your notes, flashcards, and data in a portable format
                    </p>
                    <button
                      onClick={handleExportData}
                      disabled={isExporting}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      {isExporting ? 'Preparing Export...' : 'Export All Data'}
                    </button>
                  </div>

                  <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="font-medium text-red-600 dark:text-red-400 mb-2">Danger Zone</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      Permanently delete your account and all associated data
                    </p>
                    <button
                      onClick={handleDeleteAccount}
                      className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                    >
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>

              {/* Save Settings Button */}
              <div className="flex justify-end">
                <button className="px-8 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition">
                  Save All Settings
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;